import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "nunca");
const STALE_MS = 30 * 60 * 1000;

const Down = ({ id, label, item }) => (
  <div data-testid={`panel-health-down-${id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.08] px-3 py-1.5 text-xs">
    <span className="font-semibold text-red-200">{label}</span>
    <span className="text-red-300/80">parou desde {hhmm(item.down_since)} · último OK {hhmm(item.last_ok_at)}{item.error ? ` · ${item.error}` : ""}</span>
  </div>
);

export const PanelHealthCard = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const load = () => api.get("/panels/health").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.post("/panels/health/check"); setD(data); toast.success("Verificação concluída"); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao verificar"); }
    setBusy(false);
  };
  if (!d || !d.configured) return null;

  const apps = Object.entries(d.apps || {});
  const down = apps.filter(([, a]) => !a.ok);
  const proxyDown = d.proxy && !d.proxy.ok;
  const stale = !d.checked_at || Date.now() - new Date(d.checked_at).getTime() > STALE_MS;
  const bad = proxyDown || down.length > 0;
  const tone = bad ? "border-red-500/40 bg-red-500/[0.06]" : stale ? "border-amber-500/40 bg-amber-500/[0.05]" : "border-[#1F293D] bg-[#111827]";
  const Icon = bad ? ShieldAlert : ShieldCheck;

  return (
    <div data-testid="panel-health-card" data-state={bad ? "down" : stale ? "stale" : "ok"} className={`rounded-2xl border p-4 ${tone}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Icon className={`h-5 w-5 ${bad ? "text-red-400" : stale ? "text-amber-300" : "text-emerald-400"}`} />
        <div className="min-w-0 flex-1">
          <div data-testid="panel-health-title" className="text-sm font-bold text-white">
            {proxyDown ? "Proxy parou de responder" : down.length ? `${down.length} painel(is) de app sem resposta` : stale ? "Monitor dos painéis sem verificação recente" : `Proxy e ${apps.length} painéis respondendo`}
          </div>
          <div data-testid="panel-health-checked" className="text-[11px] text-slate-500">
            Verificado {hhmm(d.checked_at)} · checagem automática a cada 10 min{bad ? " · busca de MAC e Bia podem falhar" : ""}
          </div>
        </div>
        <button data-testid="panel-health-check-btn" onClick={check} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#1F293D] px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:bg-[#1A2234] disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Verificando..." : "Verificar agora"}
        </button>
      </div>
      {d.backup && (
        <div data-testid="panel-health-backup" className={`mt-3 rounded-lg border px-3 py-1.5 text-xs ${d.backup.label ? "border-amber-500/40 bg-amber-500/[0.08] text-amber-200" : "border-red-500/40 bg-red-500/[0.08] text-red-200"}`}>
          PROXY CRM fora do ar desde {hhmm(d.backup.since)} · {d.backup.label ? `usando o reserva ${d.backup.label}${d.backup.name ? ` (${d.backup.name})` : ""}. Providencie um proxy novo.` : "nenhum proxy reserva funcionou."} O admin foi avisado por SMS.
        </div>
      )}
      {bad && (
        <div className="mt-3 space-y-1.5">
          {proxyDown && <Down id="proxy" label="Proxy residencial" item={d.proxy} />}
          {down.map(([id, a]) => <Down key={id} id={id} label={a.label} item={a} />)}
        </div>
      )}
    </div>
  );
};
